/**
 * A hundred rows, then a choice.
 *
 * The client table and the card grid both put the whole book in the DOM —
 * twenty-four columns across 1,228 rows is near enough thirty thousand cells
 * for React to reconcile on every render, which is what made Clients and
 * Dashboard feel stuck on the way in.
 *
 * The filters, the sort and every count still run over the whole book. This
 * only limits what is on screen, and it says the number out loud: a list that
 * silently stops at a hundred is a list somebody will swear has lost rows.
 *
 * It lives here rather than in each page because both tables and every page
 * that draws one want the same behaviour, and a second copy is a second
 * behaviour waiting to happen.
 */
import { useEffect, useState } from 'react';

export const PAGE = 100;

/** How many of `list` to draw, and the control that widens it. */
export function useWindowed(list) {
  const [shown, setShown] = useState(PAGE);
  /* A new list is a new question — a filter typed, a sort flipped, a page
     swapped — so it starts at the top again. */
  useEffect(() => { setShown(PAGE); }, [list]);

  const page = list.length > shown ? list.slice(0, shown) : list;
  const more = list.length - page.length;

  const control = more ? (
    <div className="morerows">
      <span>{`Showing ${page.length} of ${list.length}`}</span>
      <button type="button" className="btn" onClick={() => setShown(n => n + PAGE)}>
        Show {Math.min(PAGE, more)} more
      </button>
      <button type="button" className="btn sm" onClick={() => setShown(list.length)}>
        Show all {list.length}
      </button>
    </div>
  ) : null;

  return [page, control];
}
